import React, {
  CSSProperties,
  FC, RefObject, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import cx from 'clsx';
import { Icon } from '@mdi/react';
import Select from 'react-select';
import uniqolor from 'uniqolor';
import {
  mdiAccount, mdiBellRing, mdiChat, mdiChevronRight, mdiClipboardTextClockOutline, mdiCog, mdiMapMarker, mdiMicrophone,
  mdiPause, mdiPhone, mdiPlay, mdiRecord, mdiTimerOutline,
} from '@mdi/js';
import moment from 'moment';
import { useSelector, useStore } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocale } from 'lib';
import {
  CheckboxControl, ModalTitle, Overlay, useAnimatedOverlay, useDraggableOverlay,
} from 'components';
import { loadAssetGroupNames, stateSelector } from 'features/details/chat';
import { downloadAlarmExport } from 'features/details/exportAlarm';
import { AlarmCallType, AlarmLogEvent, AlarmLogEventKind } from '@x-guard/xgac-types/xgac';
import { RS } from 'types';
import {
  alarmLogActor, alarmLogDetail, alarmLogIcon, alarmLogSummonTitle, alarmLogTitle, isCallUnanswered, operatorDisplayNames,
} from 'features/details/chat/formatLog';
import {
  byNewest, CallCounts, CallRow, EventRow, eventRow, groupLogs, LogRow, MessageRow, messageEntries, messageRow, MessagesRow, PersonRole, personOf, SummonRow,
} from 'features/details/chat/groupLogs';
import { resultSelector as helpersSelector } from 'features/details/helpers';
import { alarmCreatorSelector } from 'features/details';
import { connectedCustomerSelector } from 'features/identity';
import { Image } from 'components/molecules';
import { Shadow } from '../shadow';
import { DateRange, DateRangeFilter } from './dateRange';
import styles from './style.module.scss';

const playLabel = 'Alarm_Detail_Chat_Play';

// Stands for the System block in the person filter.
const systemOption = 'system';

type Block = { key: string; title: string; items: LogRow[]; role?: PersonRole; isSystem?: boolean };

// Timeline: the width of a group column, and how tall a pause without events is drawn (px per minute, at most).
// Columns are as wide as the window allows up to the maximum, and only go down to the minimum (then it scrolls) when they must.
const timelineColumnMin = 260;
const timelineColumnMax = 480;
const timelineTimeWidth = 76;
const timelineGap = 12;
const pausePixelsPerMinute = 2;
const maxPausePixels = 120;
// The moment of a row is read at this height from its top (where its first line is).
const timelineLabelOffset = 14;

// The menus open inside the modal (a portal would count as a click outside and close it).
const selectStyles = {
  container: (base: object) => ({ ...base, flex: 1, minWidth: 0 }),
  control: (base: object) => ({ ...base, minHeight: 32, fontSize: 14 }),
  menu: (base: object) => ({ ...base, zIndex: 3, fontSize: 14 }),
};

const formatLogTime = (createdAt: string): string => {

  const value = moment(createdAt);

  if (value.isSame(moment(), 'day')) return value.format('HH:mm');

  return value.format('DD-MM-YYYY HH:mm');

};

const formatClock = (at: string): string => moment(at).format('HH:mm:ss');

const formatSeconds = (totalSeconds: number): string => {

  const total = Math.max(0, Math.round(totalSeconds));
  const pad = (value: number) => String(value).padStart(2, '0');
  const hours = Math.floor(total / 3600);
  const minutes = pad(Math.floor((total % 3600) / 60));
  const seconds = pad(total % 60);

  return hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;

};

const formatDuration = (from: string, to: string): string => formatSeconds((Date.parse(to) - Date.parse(from)) / 1000);

type RowPoint = { at: number; y: number; top: number; bottom: number };

/** The time at height `y` in a group: rows are newest first, so time runs backwards down the group. */
const timeAtY = (points: RowPoint[], y: number): number | undefined => {

  if (points.length === 0) return undefined;

  const last = points[points.length - 1];

  if (y <= points[0].y) return points[0].at;
  if (y >= last.y) return last.at;

  const index = points.findIndex((point, i) => y >= point.y && y < points[i + 1].y);
  const [from, to] = [points[index], points[index + 1]];

  return from.at + (to.at - from.at) * ((y - from.y) / (to.y - from.y));

};

/** The height of `time` in a group (between the rows around it), or the group's edge when it lies outside its rows. */
const yAtTime = (points: RowPoint[], time: number): { y: number; outside: boolean } | undefined => {

  if (points.length === 0) return undefined;

  const last = points[points.length - 1];

  if (time > points[0].at) return { y: points[0].top, outside: true };
  if (time < last.at) return { y: last.bottom, outside: true };

  const index = points.findIndex((point, i) => time <= point.at && (i === points.length - 1 || time >= points[i + 1].at));
  const [from, to] = [points[index], points[index + 1]];

  if (!to || from.at === to.at) return { y: from.y, outside: false };

  return { y: from.y + (to.y - from.y) * ((from.at - time) / (from.at - to.at)), outside: false };

};

// One voice message plays at a time.
let playingAudio: HTMLAudioElement | null = null;

const PlayButton: FC<{ src: string; label: string }> = ({ src, label }) => {

  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  // The element is captured: React has cleared the ref by the time this cleanup runs.
  useEffect(() => {

    const audio = audioRef.current;

    return () => audio?.pause();

  }, []);

  const toggle = () => {

    const audio = audioRef.current;

    if (!audio) return;

    if (!audio.paused) {

      audio.pause();
      return;

    }

    if (playingAudio && playingAudio !== audio) playingAudio.pause();

    playingAudio = audio;
    audio.play().catch(() => setPlaying(false));

  };

  return (
    <>
      <button type="button" className={styles.play} aria-label={label} title={label} onClick={toggle}>
        <Icon path={playing ? mdiPause : mdiPlay} size="14px"/>
      </button>
      <audio
        ref={audioRef}
        src={src}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </>
  );

};

// The same color per asset group as the alarm app shows in its group settings.
const groupColor = (id: string) => uniqolor(id, { saturation: [100, 100], lightness: [10, 80] });

const GroupLabel: FC<{ id: string; name: string }> = ({ id, name }) => {

  const { color, isLight } = groupColor(id);

  return <span className={styles.groupLabel} style={{ backgroundColor: color, color: isLight ? '#000' : '#fff' }}>{name}</span>;

};

const initials = (name: string) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase() ?? '')
  .join('');

const GroupAvatar: FC<{ name: string; image?: string; system?: boolean }> = ({ name, image, system }) => (
  <div className={styles.avatar}>
    {image && <Image src={image} alt="" className={styles.avatarImage}/>}
    {!image && system && <Icon path={mdiCog} size="16px"/>}
    {!image && !system && (initials(name) || <Icon path={mdiAccount} size="16px"/>)}
  </div>
);

export const Logs: FC<{ containerRef: RefObject<HTMLDivElement> }> = ({ containerRef }) => {

  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [grouped, setGrouped] = useState(true);
  const [timeline, setTimeline] = useState(true);
  // The timeline takes the whole window.
  const timelineMode = grouped && timeline;
  const { shouldRender, overlayProps } = useAnimatedOverlay(open);

  const fullRef = useRef<HTMLDivElement>(null);

  const store = useStore<RS>();
  const [exporting, setExporting] = useState(false);
  const draggableProps = useDraggableOverlay({ containerRef, handleSelector: '[data-drag-handle]' });
  const {
    logs, operatorMessages, status, backfilling, callEvents, participants, alarmCallEvents, alarmCallParticipants,
  } = useSelector(stateSelector);
  const phoneParticipants = useSelector((state: RS) => state.details.phoneCalls.participants);
  const operatorNames = operatorDisplayNames(
    [...operatorMessages, ...logs],
    t('Alarm_Detail_Logs_Operator'),
    (number) => t('Alarm_Detail_Logs_OperatorNumber', { number }),
  );
  const seenIds = useRef<Set<string> | null>(null);
  const wasBackfilling = useRef(false);
  const [freshIds, setFreshIds] = useState<ReadonlySet<string>>(() => new Set());
  const alarmHelpers = useSelector(helpersSelector);
  const creator = useSelector(alarmCreatorSelector);
  const alarmCreatedAt = useSelector((state: RS) => state.details.value?.alarm?.createdAt);
  const alarmCenter = useSelector((state: RS) => state.details.value?.alarm?.alarmCenter);
  const customer = useSelector(connectedCustomerSelector);
  // Operators of the alarm center show with their name and are its dispatchers; for anyone else they are anonymous operators.
  const fromAlarmCenter = !!alarmCenter && alarmCenter._id === customer?._id;
  const messages = messageEntries(
    [...callEvents, ...alarmCallEvents],
    [...phoneParticipants, ...participants, ...alarmCallParticipants],
    operatorMessages,
    (event) => alarmLogActor(event, t, operatorNames),
    t('Alarm_Detail_Export.Name_Anonymous'),
    creator,
    operatorNames,
  );
  // A chat log event written for a call chat message repeats that message, which is listed on its own.
  const messageIds = new Set(messages.map(({ id }) => id));
  // Date range filter; an empty end is open.
  const [range, setRange] = useState<DateRange>({});
  const inRange = ({ at }: { at: string }) => (
    (!range.from || Date.parse(at) >= range.from.getTime()) && (!range.to || Date.parse(at) <= range.to.getTime())
  );
  // The alarm has no log event for its own creation: add it, so every log starts with the creator making the alarm.
  const createdEvent = alarmCreatedAt && creator?.name ? ({
    _id: 'alarm-created',
    createdAt: alarmCreatedAt,
    createdBy: { _id: creator._id, _ref: 'Asset', name: creator.name },
    data: { eventType: 'alarmCreated' },
  } as unknown as AlarmLogEvent) : undefined;
  const allLogs = createdEvent ? [...logs, createdEvent] : logs;
  const shownLogs = allLogs.filter(({ createdAt }) => inRange({ at: createdAt }));
  const shownMessages = messages.filter(inRange);
  const sinceUntil = (rows: LogRow[]) => rows.filter(inRange);
  const allChronological = [
    ...shownLogs
      .filter(({ data }) => !(data?.eventType === AlarmLogEventKind.Chat && data.alarmCallEvent && messageIds.has(data.alarmCallEvent._id)))
      .map(eventRow),
    ...shownMessages.map(messageRow),
  ].sort(byNewest);
  const helperNames = new Set(alarmHelpers.flatMap(({ asset }) => (asset?.name ? [asset.name] : [])));
  // Only the open modal reads the groups.
  // Grouped from everything, then cut by date: a call whose join or leave lies outside the range keeps its real times.
  const everything = open
    ? groupLogs(allLogs, operatorNames, messages, { creatorName: creator?.name, helperNames })
    : { system: [], people: [] };
  const system = sinceUntil(everything.system);
  const people = everything.people
    .map((person) => ({ ...person, rows: sinceUntil(person.rows) }))
    .filter(({ rows }) => rows.length > 0);

  // Selected people plus the members of selected asset groups stay visible.
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [groupNames, setGroupNames] = useState<ReadonlyMap<string, string>>(() => new Map());
  const groupsOfName = new Map<string, string[]>();

  [...alarmHelpers.map(({ asset }) => asset), creator].forEach((asset) => {

    const refs = (asset as { assetGroups?: { _id: string }[] } | null | undefined)?.assetGroups;

    if (asset?.name && refs) groupsOfName.set(asset.name, refs.map(({ _id }) => _id));

  });

  const groupIds = [...new Set([...groupsOfName.values()].flat())].sort().join(',');

  useEffect(() => {

    if (!open || !groupIds) return undefined;

    const controller = new AbortController();

    loadAssetGroupNames(groupIds.split(','), controller.signal)
      .then(setGroupNames)
      .catch(() => undefined);

    return () => controller.abort();

  }, [open, groupIds]);

  const images = new Map<string, string>();

  alarmHelpers.forEach(({ asset }) => asset?.properties?.image && images.set(asset.name, asset.properties.image));
  logs.forEach(({ createdBy }) => createdBy?.properties?.image && !images.has(createdBy.name) && images.set(createdBy.name, createdBy.properties.image));

  const assetOptions = [
    { value: systemOption, label: t('Alarm_Detail_Logs_System') },
    // From everyone, not only those in the date range: a selected person must stay in the select after the range changes.
    ...everything.people.map(({ name: label }) => ({ value: label, label })),
  ];
  const assetOptionLabel = ({ value, label }: { value: string; label: string }) => (
    <span className={styles.optionLabel}>
      <GroupAvatar name={label} image={images.get(value)} system={value === systemOption}/>
      {label}
    </span>
  );

  const filtering = (selectedPeople.length > 0 || selectedGroups.length > 0);
  const visibleNames = new Set([
    ...selectedPeople,
    ...[...groupsOfName].filter(([, ids]) => ids.some((id) => selectedGroups.includes(id))).map(([name]) => name),
  ]);
  const visiblePeople = filtering ? people.filter(({ name }) => visibleNames.has(name)) : people;
  const chronological = filtering
    ? allChronological.filter((row) => {

      const person = row.kind === 'message' ? row.entry.person : personOf((row as EventRow).event, operatorNames);

      return visibleNames.has(person ?? systemOption);

    })
    : allChronological;

  useEffect(() => {

    if (!status.fulfilled) return;

    const ids = logs.map((event) => event._id);

    if (!seenIds.current) {

      seenIds.current = new Set(ids);
      return;

    }

    const arrived = ids.filter((id) => !seenIds.current?.has(id));
    // Older rows that are still being loaded are not new.
    const history = backfilling || wasBackfilling.current;

    wasBackfilling.current = backfilling;
    ids.forEach((id) => seenIds.current?.add(id));

    if (arrived.length === 0 || history) return;

    setFreshIds((current) => new Set([...current, ...arrived]));

  }, [backfilling, logs, status]);

  const tickets = ({ confirmedAt, viewedAt }: EventRow) => (
    <>
      {confirmedAt && <span className={styles.ticket} title={formatLogTime(confirmedAt)}>{t('Alarm_Detail_Logs_Confirmed')}</span>}
      {viewedAt && <span className={styles.ticket} title={formatLogTime(viewedAt)}>{t('Alarm_Detail_Logs_ViewedTicket')}</span>}
    </>
  );

  const eventRowView = (row: EventRow) => {

    const { event } = row;
    const detail = alarmLogDetail(event, t);

    return (
      <div key={row.id} data-at={Date.parse(row.at)} className={cx(styles.row, freshIds.has(event._id) && styles.fresh)}>
        <Icon path={alarmLogIcon(event)} size="16px"/>
        <div className={styles.body}>
          <div className={styles.line}>
            <span className={styles.actor}>{alarmLogActor(event, t, operatorNames)}</span>
            <time className={styles.time}>{formatLogTime(event.createdAt)}</time>
          </div>
          <div className={styles.title}>
            {alarmLogTitle(event, t)}
            {tickets(row)}
          </div>
          {detail && <div className={styles.detail}>{detail}</div>}
        </div>
      </div>
    );

  };

  const messageCounts = ({
    chat, audio, position, recording, recordingMs,
  }: CallCounts) => (
    <>
      {chat > 0 && (
        <li className={styles.muted}>
          ({t(chat === 1 ? 'Alarm_Detail_Logs_ChatMessages_One' : 'Alarm_Detail_Logs_ChatMessages_Other', { count: chat })})
        </li>
      )}
      {audio > 0 && (
        <li className={styles.muted}>
          ({t(audio === 1 ? 'Alarm_Detail_Logs_AudioMessages_One' : 'Alarm_Detail_Logs_AudioMessages_Other', { count: audio })})
        </li>
      )}
      {position > 0 && (
        <li className={styles.muted}>
          ({t(position === 1 ? 'Alarm_Detail_Logs_PositionUpdates_One' : 'Alarm_Detail_Logs_PositionUpdates_Other', { count: position })})
        </li>
      )}
      {recording > 0 && (
        <li className={styles.muted}>({t('Alarm_Detail_Logs_RecordingCount', { duration: formatSeconds(recordingMs / 1000) })})</li>
      )}
    </>
  );

  const entryIcon = (kind: MessageRow['entry']['kind']) => ({
    chat: mdiChat, audio: mdiMicrophone, position: mdiMapMarker, recording: mdiRecord,
  }[kind]);

  const entryTitle = (kind: MessageRow['entry']['kind']) => t({
    chat: 'Alarm_Detail_Logs_Chat',
    audio: 'Alarm_Detail_Logs_MessageAudio',
    position: 'Alarm_Detail_Chat_Position',
    recording: 'Alarm_Detail_Logs_Recording',
  }[kind] as 'Alarm_Detail_Logs_Chat');

  const messageRowView = (row: MessageRow) => (
    <div key={row.id} data-at={Date.parse(row.at)} className={styles.row}>
      <Icon path={entryIcon(row.entry.kind)} size="16px"/>
      <div className={styles.body}>
        <div className={styles.line}>
          <span className={styles.actor}>{row.entry.person}</span>
          <time className={styles.time}>{formatLogTime(row.at)}</time>
        </div>
        <div className={styles.title}>{entryTitle(row.entry.kind)}</div>
        {(row.entry.text || row.entry.src) && (
          <div className={cx(styles.detail, styles.message)}>
            {row.entry.src && <PlayButton src={row.entry.src} label={t(playLabel)}/>}
            {row.entry.text}
          </div>
        )}
        {row.entry.kind === 'recording' && (
          <div className={styles.detail}>{formatSeconds((row.entry.durationMs ?? 0) / 1000)}</div>
        )}
      </div>
    </div>
  );

  const messagesRowView = (row: MessagesRow) => (
    <div key={row.id} data-at={Date.parse(row.at)} className={styles.row}>
      <Icon path={row.counts.audio > 0 && row.counts.chat === 0 ? mdiMicrophone : mdiChat} size="16px"/>
      <div className={styles.body}>
        <div className={styles.line}>
          <span className={styles.actor}>{t('Alarm_Detail_Logs_Messages')}</span>
          <time className={styles.time}>{formatLogTime(row.at)}</time>
        </div>
        <ul className={styles.steps}>{messageCounts(row.counts)}</ul>
      </div>
    </div>
  );

  const callRowView = (row: CallRow) => (
    <div key={row.id} data-at={Date.parse(row.at)} className={cx(styles.row, row.ids.some((id) => freshIds.has(id)) && styles.fresh)}>
      <Icon path={mdiPhone} size="16px"/>
      <div className={styles.body}>
        <div className={styles.line}>
          <span className={styles.actor}>{t('Alarm_Detail_Logs_CallConnected')}</span>
          {row.callType && (
            <span className={styles.role}>
              {t(row.callType === AlarmCallType.AlarmCall ? 'Alarm_Detail_Logs_CallType_alarmCall' : 'Alarm_Detail_Logs_CallType_helperCall')}
            </span>
          )}
          <time className={styles.time}>{formatLogTime(row.at)}</time>
        </div>
        <ul className={styles.steps}>
          {/* Only shown when nobody joined: otherwise the first join says it all. */}
          {row.startedAt && row.intervals.length === 0 && (
            <li><span>{t('Alarm_Detail_Logs_CallStarted')}</span><time>{formatClock(row.startedAt)}</time></li>
          )}
          {row.intervals.map((interval) => (
            <li key={interval.from}>
              <span>
                {formatClock(interval.from)} – {interval.to ? formatClock(interval.to) : t('Alarm_Detail_Logs_CallOngoing')}
                {interval.to && (
                  <span className={styles.duration}>
                    <Icon path={mdiTimerOutline} size="14px"/>
                    {formatDuration(interval.from, interval.to)}
                  </span>
                )}
              </span>
            </li>
          ))}
          {messageCounts(row.counts)}
        </ul>
      </div>
    </div>
  );

  const summonRowView = (row: SummonRow) => (
    <div key={row.id} data-at={Date.parse(row.at)} className={cx(styles.row, row.ids.some((id) => freshIds.has(id)) && styles.fresh)}>
      <Icon path={mdiBellRing} size="16px"/>
      <div className={styles.body}>
        <div className={styles.line}>
          <span className={styles.actor}>{t('Alarm_Detail_Logs_Summoned')}</span>
          {row.helping && <span className={styles.badge}>{t('Alarm_Detail_Logs_Helping')}</span>}
          <time className={styles.time}>{formatLogTime(row.at)}</time>
        </div>
        <ul className={styles.steps}>
          {row.lines.map((line) => (
            <li key={line.id}>
              <span>
                <Icon path={alarmLogIcon(line.event)} size="14px"/>
                {isCallUnanswered(line.event)
                  ? <span className={cx(styles.badge, styles.badgeAlert)}>{alarmLogSummonTitle(line.event, t)}</span>
                  : alarmLogSummonTitle(line.event, t)}
                {line.confirmedAt && <span className={styles.badge} title={formatLogTime(line.confirmedAt)}>{t('Alarm_Detail_Logs_Confirmed')}</span>}
              </span>
              <time>{formatClock(line.at)}</time>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const rows = (items: LogRow[]) => items.map((row) => {

    if (row.kind === 'call') return callRowView(row);
    if (row.kind === 'summon') return summonRowView(row);
    if (row.kind === 'message') return messageRowView(row);
    if (row.kind === 'messages') return messagesRowView(row);

    return eventRowView(row);

  });

  const showSystem = (!filtering || visibleNames.has(systemOption)) && system.length > 0;
  const nothingToShow = grouped ? !showSystem && visiblePeople.length === 0 : chronological.length === 0;

  // All groups next to each other on one time axis: a row sits at its moment, so a pause shows as empty space.
  // Known limit: moments are placed per minute and a pause is drawn at most `maxPausePixels` tall, so it is not to scale.
  const timelineView = (
    blocks: Block[],
    groupHeader: (title: string, count: number, role?: PersonRole, isSystem?: boolean, className?: string, style?: CSSProperties) => JSX.Element,
  ) => {

    const minuteOf = (at: string) => Math.floor(Date.parse(at) / 60000);
    const minutes = [...new Set(blocks.flatMap(({ items }) => items.map(({ at }) => minuteOf(at))))].sort((a, b) => b - a);
    // Grid row 1 is the header; every moment gets a row, with a pause row in between when there is a gap.
    const templates = ['auto'];
    const rowOfMinute = new Map<number, number>();
    const pauseRows: number[] = [];

    minutes.forEach((minute, index) => {

      const pause = index > 0 ? minutes[index - 1] - minute : 0;

      if (pause > 1) {

        templates.push(`${Math.min(maxPausePixels, pause * pausePixelsPerMinute)}px`);
        pauseRows.push(templates.length);

      }

      templates.push('auto');
      rowOfMinute.set(minute, templates.length);

    });

    return (
      <div
        className={styles.timeline}
        style={{
          gridTemplateColumns: `${timelineTimeWidth}px repeat(${blocks.length}, minmax(${timelineColumnMin}px, ${timelineColumnMax}px))`,
          gridTemplateRows: templates.join(' '),
          minWidth: timelineTimeWidth + blocks.length * (timelineColumnMin + timelineGap),
        }}
      >
        <div className={styles.timelineGutter} style={{ gridRow: '2 / -1', gridColumn: 1 }}/>
        <div className={styles.timelineCorner} style={{ gridRow: 1, gridColumn: 1 }}/>
        {blocks.map(({ key }, index) => <div key={key} className={styles.timelineLane} style={{ gridRow: '1 / -1', gridColumn: index + 2 }}/>)}
        {/* The scale: a faint line where the hour changes, dashes in a pause. Events sit on top of it. */}
        {minutes.map((minute, index) => index > 0 && Math.floor(minutes[index - 1] / 60) !== Math.floor(minute / 60) && (
          <div key={`tick-${minute}`} className={styles.timelineTick} style={{ gridRow: rowOfMinute.get(minute), gridColumn: '2 / -1' }}/>
        ))}
        {pauseRows.map((row) => (
          <React.Fragment key={`pause-${row}`}>
            {blocks.map(({ key }, index) => (
              <div key={key} className={styles.timelinePauseScale} style={{ gridRow: row, gridColumn: index + 2 }}/>
            ))}
            <div className={styles.timelinePause} style={{ gridRow: row, gridColumn: 1 }}/>
          </React.Fragment>
        ))}
        {minutes.map((minute) => (
          <div
            key={`axis-${minute}`}
            className={styles.timelineAxis}
            data-axis={minute * 60000}
            style={{ gridRow: rowOfMinute.get(minute), gridColumn: 1 }}
          >
            {formatLogTime(new Date(minute * 60000).toISOString())}
          </div>
        ))}
        <div className={styles.timelineCursor} data-line data-cursor><span/></div>
        {blocks.map(({
          key, title, items, role, isSystem,
        }, index) => (
          <React.Fragment key={key}>
            {groupHeader(title, items.length, role, isSystem, undefined, { gridRow: 1, gridColumn: index + 2, zIndex: 3 })}
            {minutes.filter((minute) => items.some(({ at }) => minuteOf(at) === minute)).map((minute) => (
              <div key={minute} className={styles.timelineCell} style={{ gridRow: rowOfMinute.get(minute), gridColumn: index + 2 }}>
                {rows(items.filter(({ at }) => minuteOf(at) === minute))}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    );

  };

  const groupedRows = () => {

    const roleLabel = (role?: PersonRole) => {

      if (role === 'creator') return t('Alarm_Detail_Logs_Role_creator');
      if (role === 'helper') return t('Alarm_Detail_Logs_Role_helper');
      if (role === 'operator') return t(fromAlarmCenter ? 'Alarm_Detail_Logs_Role_centralist' : 'Alarm_Detail_Logs_Role_operator');

      return undefined;

    };
    const groupHeader = (title: string, count: number, role?: PersonRole, isSystem?: boolean, className?: string, style?: CSSProperties) => (
      <header className={cx(styles.groupHeader, className)} style={style}>
        <GroupAvatar name={title} image={images.get(title)} system={isSystem}/>
        <span className={styles.groupName}>{title}</span>
        {roleLabel(role) && <span className={styles.role}>{roleLabel(role)}</span>}
        {(groupsOfName.get(title) ?? []).map((id) => groupNames.has(id) && <GroupLabel key={id} id={id} name={groupNames.get(id) ?? ''}/>)}
        <span className={styles.groupCount}>{count}</span>
      </header>
    );
    const block = ({
      key, title, items, role, isSystem,
    }: Block) => (
      <section key={key} className={styles.group} data-group>
        <div className={styles.timeLine} data-line><span/></div>
        {groupHeader(title, items.length, role, isSystem)}
        {rows(items)}
      </section>
    );

    const systemBlocks: Block[] = showSystem ? [{
      key: 'system', title: t('Alarm_Detail_Logs_System'), items: system, isSystem: true,
    }] : [];
    const personBlocks: Block[] = visiblePeople.map((person) => ({
      key: `person-${person.name}`, title: person.name, items: person.rows, role: person.role,
    }));
    const blocks: Block[] = [...systemBlocks, ...personBlocks];
    // The timeline always reads left to right: the alarm creator, the system, then the rest.
    const timelineBlocks: Block[] = [
      ...personBlocks.filter(({ role }) => role === 'creator'),
      ...systemBlocks,
      ...personBlocks.filter(({ role }) => role !== 'creator'),
    ];

    return timelineMode ? timelineView(timelineBlocks, groupHeader) : <div>{blocks.map(block)}</div>;

  };

  const pointer = useRef<{ x: number; y: number } | null>(null);

  const hideTimeLines = () => fullRef.current?.querySelectorAll<HTMLElement>('[data-line]').forEach((line) => {

    line.style.display = 'none';

  });

  // One horizontal line per group at the same moment in time as the pointer, to compare groups while reading.
  // Everything is measured first and written after, once per frame, so moving the pointer does not force repeated layouts.
  const moveTimeLines = () => {

    const at = pointer.current;

    if (timelineMode) {

      // One line over all groups: the rows are aligned in time, so the moment follows from the moments on the axis.
      const cursor = fullRef.current?.querySelector<HTMLElement>('[data-cursor]');
      const points = [...(fullRef.current?.querySelectorAll<HTMLElement>('[data-axis]') ?? [])].map((axis): RowPoint => {

        const rect = axis.getBoundingClientRect();

        return {
          at: Number(axis.dataset.axis), y: rect.top + timelineLabelOffset, top: rect.top, bottom: rect.bottom,
        };

      });

      if (!cursor?.parentElement || !at) return;

      // Above the first and below the last moment there is nothing to read.
      if (points.length === 0 || at.y < points[0].top || at.y > points[points.length - 1].bottom) {

        cursor.style.display = 'none';
        return;

      }

      cursor.style.display = 'block';
      cursor.style.top = `${at.y - cursor.parentElement.getBoundingClientRect().top}px`;
      cursor.querySelector('span')?.replaceChildren(formatClock(new Date(timeAtY(points, at.y) ?? 0).toISOString()));
      return;

    }

    const hovered = at && document.elementFromPoint(at.x, at.y)?.closest<HTMLElement>('[data-group]');
    const measured = [...(fullRef.current?.querySelectorAll<HTMLElement>('[data-group]') ?? [])].map((group) => ({
      group,
      line: group.querySelector<HTMLElement>('[data-line]'),
      top: group.getBoundingClientRect().top,
      points: [...group.querySelectorAll<HTMLElement>('[data-at]')].map((row): RowPoint => {

        const rect = row.getBoundingClientRect();

        return {
          at: Number(row.dataset.at), y: rect.top + rect.height / 2, top: rect.top, bottom: rect.bottom,
        };

      }),
    }));
    const time = at && hovered ? timeAtY(measured.find(({ group }) => group === hovered)?.points ?? [], at.y) : undefined;

    // Between groups there is no time to read: keep the lines where they are instead of flickering.
    if (!at || time === undefined) return;

    measured.forEach(({
      group, line, top, points,
    }) => {

      const isHovered = group === hovered;
      const found = isHovered ? { y: at.y, outside: false } : yAtTime(points, time);

      if (!line) return;

      // No line when the moment lies before or after everything in this group: it would stick to the group's edge.
      if (!found || found.outside) {

        line.style.display = 'none';
        return;

      }

      line.style.display = 'block';
      // The hovered group's line sticks to the pointer; the others glide, since a small move can span a long time.
      line.style.transition = isHovered ? 'none' : 'top 120ms ease-out';
      line.style.top = `${found.y - top}px`;
      line.querySelector('span')?.replaceChildren(formatClock(new Date(time).toISOString()));

    });

  };

  const frame = useRef(0);
  const scheduleTimeLines = () => {

    if (frame.current) return;

    frame.current = window.requestAnimationFrame(() => {

      frame.current = 0;
      moveTimeLines();

    });

  };

  useEffect(() => () => window.cancelAnimationFrame(frame.current), []);

  // Escape closes the log, unless something inside (an open select menu) already used the key.
  useEffect(() => {

    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {

      if (event.key === 'Escape' && !event.defaultPrevented) setOpen(false);

    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);

  }, [open]);

  const exportAlarm = async () => {

    setExporting(true);

    try {

      await downloadAlarmExport(store.getState(), t);

    } catch {

      toast.error(t('Alarm_Detail_Export.Failed'));

    } finally {

      setExporting(false);

    }

  };

  // Exporting while these are still loading would write them as empty.
  const loading = useSelector((state: RS) => {

    const { helpers, response, chat } = state.details;

    return chat.backfilling || [chat, helpers, response.static, response.dynamic].some((slice) => slice.status.pending);

  });

  const exportButton = (
    <button type="button" className={styles.export} disabled={exporting || loading} onClick={exportAlarm}>
      {t('Alarm_Detail_Export.Button')}
    </button>
  );

  const modal = shouldRender && containerRef.current ? createPortal(
    <>
      {!timelineMode && <Shadow enabled={open}/>}
      <Overlay
        onDismiss={() => setOpen(false)}
        {...draggableProps}
        {...overlayProps({ modal: true, centered: false, className: cx(styles.overlay, timelineMode && styles.fullscreen) })}
      >
        <ModalTitle dragHandle text={t('Alarm_Detail_Logs_FullTitle')} onClose={() => setOpen(false)}/>
        <div className={styles.filters}>
          <Select
            isMulti
            isClearable
            styles={selectStyles}
            placeholder={t('Alarm_Detail_Logs_Filter_People')}
            options={assetOptions}
            formatOptionLabel={assetOptionLabel}
            value={assetOptions.filter(({ value }) => selectedPeople.includes(value))}
            onChange={(chosen: { value: string }[] | null) => setSelectedPeople((chosen ?? []).map(({ value }) => value))}
          />
          {groupNames.size > 0 && (
            <Select
              isMulti
              isClearable
              styles={selectStyles}
              placeholder={t('Alarm_Detail_Logs_Filter_Groups')}
              options={[...groupNames].map(([value, label]) => ({ value, label }))}
              formatOptionLabel={({ value, label }: { value: string; label: string }) => <GroupLabel id={value} name={label}/>}
              value={selectedGroups.map((value) => ({ value, label: groupNames.get(value) ?? value }))}
              onChange={(chosen: { value: string }[] | null) => setSelectedGroups((chosen ?? []).map(({ value }) => value))}
            />
          )}
          <DateRangeFilter value={range} onChange={setRange} since={alarmCreatedAt ? new Date(alarmCreatedAt) : undefined}/>
        </div>
        <div
          ref={fullRef}
          className={cx(styles.full, timelineMode && styles.fullTimeline)}
          onMouseMove={grouped ? (event) => {

            pointer.current = { x: event.clientX, y: event.clientY };
            scheduleTimeLines();

          } : undefined}
          onScroll={grouped ? scheduleTimeLines : undefined}
          onMouseLeave={() => {

            pointer.current = null;
            window.cancelAnimationFrame(frame.current);
            frame.current = 0;
            hideTimeLines();

          }}
        >
          {nothingToShow && !loading && <div className={styles.empty}>{t('Alarm_Detail_Logs_Empty')}</div>}
          {grouped ? groupedRows() : rows(chronological)}
        </div>
        <div className={styles.footer}>
          <div className={styles.options}>
            <CheckboxControl
              size={'small'}
              label={t('Alarm_Detail_Logs_Group')}
              input={{ checked: grouped, onChange: () => setGrouped((value) => !value) }}
            />
            {grouped && (
              <CheckboxControl
                size={'small'}
                label={t('Alarm_Detail_Logs_Timeline')}
                input={{ checked: timeline, onChange: () => setTimeline((value) => !value) }}
              />
            )}
          </div>
          {exportButton}
        </div>
      </Overlay>
    </>,
    // The timeline lives on the body: inside the page it would stay below the header and the banner.
    timelineMode ? document.body : containerRef.current,
  ) : null;

  // The log itself is only shown in the modal; the screen just shows that it exists and how much it holds.
  return (
    <section>
      <button type="button" className={styles.open} title={t('Alarm_Detail_Logs_ShowMore')} onClick={() => setOpen(true)}>
        <Icon path={mdiClipboardTextClockOutline} size="16px"/>
        <span className={styles.openTitle}>{t('Alarm_Detail_Logs_Title')}</span>
        {status.fulfilled && (
          <span className={styles.openCount}>
            {t(logs.length === 1 ? 'Alarm_Detail_Logs_Count_One' : 'Alarm_Detail_Logs_Count_Other', { count: logs.length })}
          </span>
        )}
        <Icon path={mdiChevronRight} size="16px"/>
      </button>
      {modal}
    </section>
  );

};
